/**
 * Ganancias: ingresos, costo real y lo que de verdad queda (Req. 16).
 *
 * CODIGO PURO. No conoce la base de datos.
 *
 * LA IDEA QUE ESTA FASE EXISTE PARA CAPTURAR:
 *
 * Un negocio de comida no gana "precio menos costo por porcion". Gana lo que
 * cobra menos lo que gasto en TODO lo que cocino, incluidas las porciones que
 * no vendio. Si hace 6 porciones de birria, vende 4 y se comen 2 en casa, el
 * gasto de las 6 ya salio de su bolsa.
 *
 * Por eso se reportan dos numeros distintos y ninguno sobra:
 *
 *   ganancia de lo vendido = ingreso - costo de lo vendido
 *                            (el margen del platillo: sirve para poner precio)
 *
 *   resultado neto         = ingreso - costo de lo vendido - costo no vendido
 *                            (lo que quedo en la bolsa: sirve para saber si el
 *                             negocio deja dinero)
 *
 * Mostrar solo el primero haria ver rentable un dia en que se regalo media
 * produccion. Mostrar solo el segundo haria imposible fijar precios.
 *
 * EL PRORRATEO SE HACE POR RESTA, no dividiendo dos veces:
 *
 *   costoVendido   = redondear(costoTotal * vendidas / producidas)
 *   costoNoVendido = costoTotal - costoVendido
 *
 * Redondear las dos partes por separado no suma el total. Con un costo de 1
 * centavo y 1 de 2 porciones vendidas, redondear 0.5 dos veces da 2 centavos de
 * un costo de 1. La resta garantiza que las partes sumen exacto siempre.
 */

import { ErrorValidacion } from './errors';
import {
  inicioSemana,
  finSemana,
  inicioMes,
  finMes,
  semanaDelMes,
  type FechaLocal,
} from './dates';
import type { Centavos } from './money';

// ---------------------------------------------------------------------------
// Prorrateo
// ---------------------------------------------------------------------------

export interface CostoProrrateado {
  vendidoCentavos: Centavos;
  noVendidoCentavos: Centavos;
}

/**
 * Reparte el costo de una produccion entre lo vendido y lo que no se vendio.
 *
 * Verificacion del planteamiento: costo $275.20, 7 de 8 porciones vendidas.
 *   vendido   = redondear(27520 * 7 / 8) = 24080  ($240.80)
 *   no vendido= 27520 - 24080            =  3440  ($34.40)
 *   suma                                 = 27520  (exacto)
 */
export function prorratearCosto(
  costoTotalCentavos: Centavos,
  porcionesVendidas: number,
  porcionesProducidas: number,
): CostoProrrateado {
  if (porcionesProducidas <= 0) {
    throw new ErrorValidacion('Las porciones producidas deben ser mayores que cero.');
  }
  if (porcionesVendidas < 0) {
    throw new ErrorValidacion('Las porciones vendidas no pueden ser negativas.');
  }

  // Vender mas de lo producido no deberia pasar, pero si pasa no se inventa un
  // costo mayor al real: se reconoce todo el costo como vendido.
  const vendidas = Math.min(porcionesVendidas, porcionesProducidas);

  const vendidoCentavos = Math.round((costoTotalCentavos * vendidas) / porcionesProducidas);

  return {
    vendidoCentavos,
    noVendidoCentavos: costoTotalCentavos - vendidoCentavos,
  };
}

// ---------------------------------------------------------------------------
// Resultado de un lote
// ---------------------------------------------------------------------------

export interface DatosLote {
  porcionesProducidas: number;
  porcionesVendidas: number;
  precioUnitarioCentavos: Centavos;
  /** Costo congelado de la produccion. null = no hubo produccion registrada. */
  costoProduccionCentavos: Centavos | null;
}

export interface ResultadoLote {
  porcionesProducidas: number;
  porcionesVendidas: number;
  porcionesNoVendidas: number;
  ingresoCentavos: Centavos;
  costoProduccionCentavos: Centavos | null;
  costoVendidoCentavos: Centavos | null;
  costoNoVendidoCentavos: Centavos | null;
  /** Ingreso menos el costo de lo vendido. Para fijar precios. */
  gananciaDeLoVendidoCentavos: Centavos | null;
  /** Ingreso menos TODO el costo producido. Lo que quedo en la bolsa. */
  resultadoNetoCentavos: Centavos | null;
  /** Margen sobre el ingreso, calculado con la ganancia de lo vendido. */
  margenPorcentaje: number | null;
  /** true cuando no hay costo del cual partir: los costos quedan en null. */
  costoNoDisponible: boolean;
}

/**
 * Calcula el resultado de un lote producido y vendido.
 *
 * Sin costo de produccion los costos quedan en `null` y no en cero, igual que en
 * el costeo de recetas: un cero haria ver una ganancia que no existe.
 */
export function resultadoDeLote(datos: DatosLote): ResultadoLote {
  const {
    porcionesProducidas,
    porcionesVendidas,
    precioUnitarioCentavos,
    costoProduccionCentavos,
  } = datos;

  const ingresoCentavos = Math.round(porcionesVendidas * precioUnitarioCentavos);
  const porcionesNoVendidas = Math.max(0, porcionesProducidas - porcionesVendidas);

  if (costoProduccionCentavos === null) {
    return {
      porcionesProducidas,
      porcionesVendidas,
      porcionesNoVendidas,
      ingresoCentavos,
      costoProduccionCentavos: null,
      costoVendidoCentavos: null,
      costoNoVendidoCentavos: null,
      gananciaDeLoVendidoCentavos: null,
      resultadoNetoCentavos: null,
      margenPorcentaje: null,
      costoNoDisponible: true,
    };
  }

  const { vendidoCentavos, noVendidoCentavos } = prorratearCosto(
    costoProduccionCentavos,
    porcionesVendidas,
    porcionesProducidas <= 0 ? Math.max(1, porcionesVendidas) : porcionesProducidas,
  );

  const gananciaDeLoVendidoCentavos = ingresoCentavos - vendidoCentavos;

  return {
    porcionesProducidas,
    porcionesVendidas,
    porcionesNoVendidas,
    ingresoCentavos,
    costoProduccionCentavos,
    costoVendidoCentavos: vendidoCentavos,
    costoNoVendidoCentavos: noVendidoCentavos,
    gananciaDeLoVendidoCentavos,
    resultadoNetoCentavos: ingresoCentavos - vendidoCentavos - noVendidoCentavos,
    margenPorcentaje:
      ingresoCentavos > 0 ? (gananciaDeLoVendidoCentavos / ingresoCentavos) * 100 : null,
    costoNoDisponible: false,
  };
}

// ---------------------------------------------------------------------------
// Agregacion por periodo
// ---------------------------------------------------------------------------

/** Una venta reducida a lo que hace falta para sumar. */
export interface VentaParaResumen {
  fecha: FechaLocal;
  porcionesVendidas: number;
  ingresoCentavos: Centavos;
  /** Costo de lo VENDIDO, ya prorrateado. null = no disponible. */
  costoCentavos: Centavos | null;
  /** true si el costo lleva estimacion (cantidades sin costo propio). */
  estimado?: boolean;
  /**
   * true si al costo le falta el precio de algun insumo.
   *
   * Distinto de `costoCentavos === null`: aqui SI hay una cifra, pero es PARCIAL
   * y por lo tanto menor que la real. Sin esta marca, una ganancia calculada
   * sobre un costo incompleto se veria firme, que es el error mas caro de esta
   * pantalla porque lleva a fijar precios bajos.
   */
  incompleto?: boolean;
}

/** Porciones que no se vendieron, con su destino y su costo. */
export interface SobranteParaResumen {
  fecha: FechaLocal;
  tipo: string;
  porciones: number;
  costoCentavos: Centavos | null;
}

export interface ResumenPeriodo {
  desde: FechaLocal;
  hasta: FechaLocal;
  porcionesVendidas: number;
  ingresoCentavos: Centavos;
  costoVendidoCentavos: Centavos;
  costoNoVendidoCentavos: Centavos;
  costoTotalCentavos: Centavos;
  gananciaDeLoVendidoCentavos: Centavos;
  resultadoNetoCentavos: Centavos;
  margenPorcentaje: number | null;
  /** Porciones no vendidas agrupadas por destino. */
  sobrantesPorDestino: Array<{ tipo: string; porciones: number; costoCentavos: Centavos }>;
  porcionesNoVendidas: number;
  /** true si alguna venta del periodo no tiene costo: los totales son parciales. */
  costoIncompleto: boolean;
  /** true si algun costo del periodo lleva estimacion. */
  costoEstimado: boolean;
  cuantasVentas: number;
}

/**
 * Suma ventas y sobrantes de un rango de fechas.
 *
 * Los costos ausentes NO se cuentan como cero: se suman solo los conocidos y el
 * resumen se marca como incompleto, igual que en el costeo de recetas.
 */
export function resumirPeriodo(
  desde: FechaLocal,
  hasta: FechaLocal,
  ventas: readonly VentaParaResumen[],
  sobrantes: readonly SobranteParaResumen[],
): ResumenPeriodo {
  const enRango = <T extends { fecha: FechaLocal }>(lista: readonly T[]): T[] =>
    lista.filter((x) => x.fecha >= desde && x.fecha <= hasta);

  const delPeriodo = enRango(ventas);
  const sobrantesDelPeriodo = enRango(sobrantes);

  const ingresoCentavos = delPeriodo.reduce((s, v) => s + v.ingresoCentavos, 0);
  const porcionesVendidas = delPeriodo.reduce((s, v) => s + v.porcionesVendidas, 0);
  const costoVendidoCentavos = delPeriodo.reduce((s, v) => s + (v.costoCentavos ?? 0), 0);
  const costoNoVendidoCentavos = sobrantesDelPeriodo.reduce(
    (s, x) => s + (x.costoCentavos ?? 0),
    0,
  );

  const porDestino = new Map<string, { porciones: number; costoCentavos: Centavos }>();
  for (const sobrante of sobrantesDelPeriodo) {
    const previo = porDestino.get(sobrante.tipo) ?? { porciones: 0, costoCentavos: 0 };
    porDestino.set(sobrante.tipo, {
      porciones: previo.porciones + sobrante.porciones,
      costoCentavos: previo.costoCentavos + (sobrante.costoCentavos ?? 0),
    });
  }

  const gananciaDeLoVendidoCentavos = ingresoCentavos - costoVendidoCentavos;

  return {
    desde,
    hasta,
    porcionesVendidas,
    ingresoCentavos,
    costoVendidoCentavos,
    costoNoVendidoCentavos,
    costoTotalCentavos: costoVendidoCentavos + costoNoVendidoCentavos,
    gananciaDeLoVendidoCentavos,
    resultadoNetoCentavos: ingresoCentavos - costoVendidoCentavos - costoNoVendidoCentavos,
    margenPorcentaje:
      ingresoCentavos > 0 ? (gananciaDeLoVendidoCentavos / ingresoCentavos) * 100 : null,
    sobrantesPorDestino: [...porDestino.entries()]
      .map(([tipo, datos]) => ({ tipo, ...datos }))
      .sort((a, b) => b.porciones - a.porciones),
    porcionesNoVendidas: sobrantesDelPeriodo.reduce((s, x) => s + x.porciones, 0),
    costoIncompleto:
      delPeriodo.some((v) => v.costoCentavos === null || v.incompleto === true) ||
      sobrantesDelPeriodo.some((x) => x.costoCentavos === null),
    costoEstimado: delPeriodo.some((v) => v.estimado === true),
    cuantasVentas: delPeriodo.length,
  };
}

// ---------------------------------------------------------------------------
// Periodos con nombre
// ---------------------------------------------------------------------------

export type Granularidad = 'dia' | 'semana' | 'mes';

export const ETIQUETA_GRANULARIDAD: Readonly<Record<Granularidad, string>> = {
  dia: 'Día',
  semana: 'Semana',
  mes: 'Mes',
};

/** Rango que cubre una granularidad alrededor de una fecha. */
export function rangoDe(
  granularidad: Granularidad,
  fecha: FechaLocal,
): { desde: FechaLocal; hasta: FechaLocal } {
  if (granularidad === 'dia') return { desde: fecha, hasta: fecha };
  if (granularidad === 'semana') {
    return { desde: inicioSemana(fecha), hasta: finSemana(fecha) };
  }
  return { desde: inicioMes(fecha), hasta: finMes(fecha) };
}

export interface TrozoSemanal {
  numero: number;
  desde: FechaLocal;
  hasta: FechaLocal;
}

/**
 * Semanas que toca un mes, recortadas al mes (Req. 16.2).
 *
 * Se recortan a proposito: una semana que empieza el 31 de julio pertenece al
 * desglose de agosto solo por los dias de agosto, o los totales por semana no
 * sumarian el total del mes.
 */
export function semanasDelMes(fecha: FechaLocal): TrozoSemanal[] {
  const desde = inicioMes(fecha);
  const hasta = finMes(fecha);

  const trozos: TrozoSemanal[] = [];
  let cursor = desde;

  while (cursor <= hasta) {
    const finDeEsta = finSemana(cursor);
    const recortado = finDeEsta > hasta ? hasta : finDeEsta;

    trozos.push({
      numero: semanaDelMes(cursor),
      desde: cursor,
      hasta: recortado,
    });

    if (recortado === hasta) break;
    cursor = siguienteDia(recortado);
  }

  return trozos;
}

function siguienteDia(fecha: FechaLocal): FechaLocal {
  const [a, m, d] = fecha.split('-').map(Number);
  const siguiente = new Date(Date.UTC(a ?? 0, (m ?? 1) - 1, (d ?? 1) + 1));
  const y = siguiente.getUTCFullYear();
  const mm = String(siguiente.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(siguiente.getUTCDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}
